import { formatGbpPence } from "../../lib/formatMoney.js";
import type { OpenRawEventsHandler, RetentionData } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { BarChart } from "../charts/BarChart.js";
import { PieChart } from "../charts/PieChart.js";
import {
  outcomeRawEventsFilters,
  OUTCOMES_CRITERIA,
  paymentOutcomePieSlices,
} from "./paymentOutcomesShared.js";

const PAYERS_CRITERIA = `device_events payment_confirmed (date + venue + device; no Game filter):

New vs Repeat — each payment_confirmed in scope; table ISID resolved like Paid session explorer: linked events on same play session id (prefer payment_succeeded), then payment_succeeded on same device within −2 min…+10 min when mobile/table psids differ. Ordered by client_timestamp within each partition: first = New, 2nd+ = Repeat. Partition = resolved ISID, else play session id, else event id. Counts payments, not distinct people.

Payment Amount — per payment_confirmed: prefer play_session_payments via checkout_id / play session id when linked; else properties.amount_pence. Bar height = confirms at that price. Total label = Gross Rev. (sum of confirmed amounts).

Bar click: Raw Events → payment_confirmed with Properties containing that amount_pence. Pie slice click: payment_confirmed (new/repeat split not reproduced in one filter).`;

function subheading(title: string) {
  return (
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
      {title}
    </div>
  );
}

interface PayersCardProps {
  data: Pick<
    RetentionData,
    "newPayers" | "repeatPayers" | "grossRevPence" | "paymentAmounts" | "paymentOutcomes"
  >;
  onOpenRawEvents: OpenRawEventsHandler;
  showCriteria?: boolean;
  /** Tighter three-across layout for half-width dashboard columns. */
  compact?: boolean;
}

export function PayersCard({
  data,
  onOpenRawEvents,
  showCriteria = true,
  compact = false,
}: PayersCardProps) {
  const amountBars = data.paymentAmounts.map((row) => ({
    label: formatGbpPence(row.amountPence),
    value: row.count,
  }));

  const payerSlices = [
    { label: "New Payers", value: data.newPayers, color: "#5aa2ff" },
    { label: "Repeat Payers", value: data.repeatPayers, color: "#31c48d" },
  ];
  const payerTotal = data.newPayers + data.repeatPayers;

  const outcomeSlices = paymentOutcomePieSlices(data.paymentOutcomes);

  const criteria = showCriteria ? `${PAYERS_CRITERIA}\n\n${OUTCOMES_CRITERIA}` : undefined;

  const pieProps = { stackedLegend: true as const, mini: compact };

  const amountBarWidth = 26;
  const amountViewBoxWidth = 140;

  const columnClass = compact ? "min-w-0" : "flex min-w-0 flex-col";

  return (
    <ChartCard
      compact={compact}
      title="Payers & mobile outcomes"
      criteria={criteria}
      note={
        compact
          ? undefined
          : "New vs repeat per interaction session id; amount bars = confirmed charges; outcomes pie = terminal mobile checkout events."
      }
    >
      <div className={`grid min-w-0 grid-cols-3 items-stretch ${compact ? "gap-2" : "gap-3"}`}>
        <div className={columnClass}>
          {subheading("New vs Repeat")}
          {payerTotal > 0 ? (
            <PieChart
              {...pieProps}
              slices={payerSlices}
              sliceTooltip={(slice) =>
                `${slice.label}: ${slice.value} — first vs repeat payment_confirmed within the same interaction_session_id. Click: Raw Events → payment_confirmed. Middle-click / Ctrl/Cmd+click: new tab.`
              }
              onSliceClick={(_slice, _i, e) =>
                onOpenRawEvents({ eventName: "payment_confirmed" }, e)
              }
            />
          ) : (
            <p className="w-full text-center text-[11px] text-text-secondary">
              No payment_confirmed rows in scope.
            </p>
          )}
        </div>

        <div className={columnClass}>
          {subheading("Mobile Payment Outcomes")}
          {outcomeSlices.length > 0 ? (
            <PieChart
              {...pieProps}
              slices={outcomeSlices}
              sliceTooltip={(slice, i) => {
                const key = data.paymentOutcomes.slices[i]?.key ?? "";
                const filters = outcomeRawEventsFilters(key);
                return `${slice.label}: ${slice.value} — Click: Raw Events → ${filters.eventName}${filters.propertiesContains ? ` (${filters.propertiesContains})` : ""}. Middle-click / Ctrl/Cmd+click: new tab.`;
              }}
              onSliceClick={(_slice, i, e) => {
                const key = data.paymentOutcomes.slices[i]?.key ?? "";
                onOpenRawEvents(outcomeRawEventsFilters(key), e);
              }}
            />
          ) : (
            <p className="w-full text-center text-[11px] text-text-secondary">
              No mobile checkout outcome events in scope.
            </p>
          )}
        </div>

        <div className={columnClass}>
          {subheading("Payment Amount")}
          <BarChart
            compact
            verbatimAxisLabels
            heightAuto
            viewBoxWidth={amountViewBoxWidth}
            maxBarWidth={amountBarWidth}
            plotCssHeight={110}
            valueLabelFontSize={11}
            valueLabelPad={6}
            data={amountBars}
            barTooltip={(item) =>
              `${item.label}: ${Math.round(item.value)} confirm(s) — click: Raw Events → payment_confirmed at this amount. Middle-click / Ctrl/Cmd+click: new tab.`
            }
            onBarClick={(_item, index, _si, e) => {
              const amountPence = data.paymentAmounts[index]?.amountPence;
              if (amountPence == null) return;
              onOpenRawEvents(
                {
                  eventName: "payment_confirmed",
                  propertiesContains: `"amount_pence":${amountPence}`,
                },
                e,
              );
            }}
          />
          <div className="mt-0.5 text-center text-[11px] font-bold text-text-primary">
            {formatGbpPence(data.grossRevPence)}
          </div>
        </div>
      </div>
    </ChartCard>
  );
}
